import { IconCoin, IconCreditCard, IconVelocimeter, IconId, IconUser, IconHelpCircle, IconLock } from '@consumidor-positivo/aurora';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTracking } from '@common/hooks/useTracking';
import isDesktop from '@libs/utils/helpers/isDesktop';
import { RouteMap } from '@common/constants/RouteMap';
import { useQuery } from '@tanstack/react-query';
import { scoreQueries } from '@common/queries/score';

import { useRenegFallback } from '@common/hooks/useRenegFallback';

import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';

export const usePrivatePagesStates = () => {
  const { elementClickedEvent } = useTracking();
  const location = useLocation();
  const navigate = useNavigate();

  const { isRenegFallback } = useRenegFallback();

  const { tokenOrigin } = useAuthenticatedUser();

  const isSimplified = tokenOrigin() === 'simplified';

  const { data: positiveCustomer } = useQuery({
    ...scoreQueries.score(),
    select: (data) => data.positiveCustomer,
  });
  const negativeCustomer = positiveCustomer === false;

  const handleNavigate = (slug: RouteMap, title: string) => {
    elementClickedEvent({
      elementType: 'button/text',
      location: location.pathname,
      position: 'navbar',
      name: slug,
      text: title,
    });

    if (slug === RouteMap.Help) {
      window.open('https://www.consumidorpositivo.com.br/central-de-ajuda');
      return;
    }

    const renegRoutes = [RouteMap.MyAgreementsAC, RouteMap.MyDebtsAC].some((path) => slug === path);

    if (renegRoutes) {
      window.location.href = slug;
      return;
    }

    navigate(slug);
  };

  const dataSourceNavbar = [
    {
      name: 'Dívidas',
      dropdown: [
        ...(!isRenegFallback
          ? [
              {
                name: 'Dívidas para negociar',
                onClick: () => handleNavigate(RouteMap.MyDebtsAC, 'Dívidas para negociar'),
                active: location.pathname === RouteMap.MyDebtsAC,
              },
              {
                name: 'Meus acordos',
                onClick: () => handleNavigate(RouteMap.MyAgreementsAC, 'Meus acordos'),
                active: location.pathname === RouteMap.MyAgreementsAC,
              },
            ]
          : [
              {
                name: 'Dívidas para negociar',
                onClick: () => handleNavigate(RouteMap.MyCpf, 'Dívidas para negociar'),
                active: location.pathname === RouteMap.MyCpf,
              },
            ]),
        {
          name: 'Dívidas negativadas',
          onClick: () => handleNavigate(RouteMap.NegativeDebts, 'Dívidas negativadas'),
          active: location.pathname === RouteMap.NegativeDebts,
        },
      ],
    },
    {
      name: 'Crédito',
      dropdown: [
        {
          name: 'Minhas ofertas',
          onClick: () => handleNavigate(RouteMap.MyOffers, 'Minhas ofertas'),
          active: location.pathname === RouteMap.MyOffers,
        },
        {
          name: 'Todas ofertas',
          onClick: () => handleNavigate(RouteMap.Offer, 'Todas ofertas'),
          active: location.pathname === RouteMap.Offer,
        },
        {
          name: 'Match Positivo',
          onClick: () => handleNavigate(RouteMap.MatchMaker, 'Match Positivo'),
          active: location.pathname === RouteMap.MatchMaker,
        },
      ],
    },
    {
      name: 'Score',
      dropdown: [
        {
          name: 'Minha pontuação',
          onClick: () => handleNavigate(RouteMap.MyScore, 'Minha pontuação'),
          active: location.pathname === RouteMap.MyScore,
        },

        ...(!negativeCustomer
          ? [
              {
                name: 'Aumentar meu Score',
                onClick: () => handleNavigate(RouteMap.Objectives, 'Aumentar meu Score'),
                active: location.pathname === RouteMap.Objectives,
              },
              {
                name: 'Histórico de Score',
                onClick: () => handleNavigate(RouteMap.ObjectivesHistoric, 'Histórico de Score'),
                active: location.pathname === RouteMap.ObjectivesHistoric,
              },
            ]
          : []),
      ],
    },
    {
      name: 'Meu CPF',
      dropdown: [
        {
          name: 'Alerta CPF',
          onClick: () => handleNavigate(RouteMap.CpfAlert, 'Alerta CPF'),
          active: location.pathname === RouteMap.CpfAlert,
        },
        {
          name: 'Consultas em meu CPF',
          onClick: () => handleNavigate(RouteMap.CpfLookout, 'Consultas em meu CPF'),
          active: location.pathname === RouteMap.CpfLookout,
        },
        {
          name: 'Cadastro positivo',
          onClick: () => handleNavigate(RouteMap.BillsAndPayments, 'Cadastro positivo'),
          active: location.pathname === RouteMap.BillsAndPayments,
        },
      ],
    },
  ];

  const itemNameDesktop = ['Minha conta', 'Alterar senha', 'Central de ajuda'];

  const dataSourceNavbarVertical = [
    {
      name: 'Dívidas',
      Icon: <IconCoin />,
      dropdown: [
        ...(!isRenegFallback
          ? [
              {
                name: 'Dívidas para negociar',
                onClick: () => handleNavigate(RouteMap.MyDebtsAC, 'Dívidas para negociar'),
                active: location.pathname === RouteMap.MyDebtsAC,
              },
              {
                name: 'Meus acordos',
                onClick: () => handleNavigate(RouteMap.MyAgreementsAC, 'Meus acordos'),
                active: location.pathname === RouteMap.MyAgreementsAC,
              },
            ]
          : [
              {
                name: 'Dívidas para negociar',
                onClick: () => handleNavigate(RouteMap.MyDebts, 'Dívidas para negociar'),
                active: location.pathname === RouteMap.MyDebts,
              },
            ]),
        {
          name: 'Dívidas negativadas',
          onClick: () => handleNavigate(RouteMap.NegativeDebts, 'Dívidas negativadas'),
          active: location.pathname === RouteMap.NegativeDebts,
        },
      ],
    },
    {
      name: 'Crédito',
      Icon: <IconCreditCard />,
      dropdown: [
        {
          name: 'Minhas ofertas',
          onClick: () => handleNavigate(RouteMap.MyOffers, 'Minhas ofertas'),
          active: location.pathname === RouteMap.MyOffers,
        },
        {
          name: 'Todas ofertas',
          onClick: () => handleNavigate(RouteMap.Offer, 'Todas ofertas'),
          active: location.pathname === RouteMap.Offer,
        },
        {
          name: 'Match Positivo',
          onClick: () => handleNavigate(RouteMap.MatchMaker, 'Match Positivo'),
          active: location.pathname === RouteMap.MatchMaker,
        },
      ],
    },
    {
      name: 'Score',
      Icon: <IconVelocimeter />,
      dropdown: [
        {
          name: 'Minha pontuação',
          onClick: () => handleNavigate(RouteMap.MyScore, 'Minha pontuação'),
          active: location.pathname === RouteMap.MyScore,
        },
        ...(!negativeCustomer
          ? [
              {
                name: 'Aumentar meu Score',
                onClick: () => handleNavigate(RouteMap.Objectives, 'Aumentar meu Score'),
                active: location.pathname === RouteMap.Objectives,
              },
              {
                name: 'Histórico de Score',
                onClick: () => handleNavigate(RouteMap.ObjectivesHistoric, 'Histórico de Score'),
                active: location.pathname === RouteMap.ObjectivesHistoric,
              },
            ]
          : []),
      ],
    },
    {
      name: 'Meu CPF',
      Icon: <IconId />,
      dropdown: [
        {
          name: 'Alerta CPF',
          onClick: () => handleNavigate(RouteMap.CpfAlert, 'Alerta CPF'),
          active: location.pathname === RouteMap.CpfAlert,
        },
        {
          name: 'Consultas em meu CPF',
          onClick: () => handleNavigate(RouteMap.CpfLookout, 'Consultas em meu CPF'),
          active: location.pathname === RouteMap.CpfLookout,
        },
        {
          name: 'Cadastro positivo',
          onClick: () => handleNavigate(RouteMap.BillsAndPayments, 'Cadastro positivo'),
          active: location.pathname === RouteMap.BillsAndPayments,
        },
      ],
    },
    {
      name: 'Minha conta',
      Icon: <IconUser />,
      onClick: () => handleNavigate(RouteMap.UserProfile, 'Minha conta'),
      active: location.pathname === RouteMap.UserProfile,
      hideOnAuthSimplified: true,
    },
    {
      name: 'Alterar senha',
      Icon: <IconLock />,
      onClick: () => handleNavigate(RouteMap.ChangePassword, 'Alterar senha'),
      active: location.pathname === RouteMap.ChangePassword,
      hideOnAuthSimplified: true,
    },
    {
      name: 'Central de ajuda',
      Icon: <IconHelpCircle />,
      onClick: () => handleNavigate(RouteMap.Help, 'Central de ajuda'),
      active: false,
    },
  ]
    .filter((item) => (isDesktop() ? itemNameDesktop.includes(item.name) : item))
    .filter((i) => !(isSimplified && i.hideOnAuthSimplified));

  return {
    dataSourceNavbar,
    dataSourceNavbarVertical,
  };
};
